/**
 * Helpers for building safe, self-describing CSV download filenames.
 *
 * Convention (matches Google Sheets `<spreadsheet> - <sheet>.csv`):
 *   - View export:  "<table> - <view>"
 *   - Table export: "<table>"
 *
 * Returned strings do NOT include the file extension — the caller
 * (`downloadTableFile` in CanvasActions.tsx) appends `.csv`/`.parquet`.
 */

// Cross-platform-illegal filename characters: Windows blocks <>:"/\|?*, NUL
// is illegal everywhere, control chars (0x00-0x1F) are also unsafe. Hyphens,
// underscores, dots, parens, and printable Unicode (including spaces) are fine.
// eslint-disable-next-line no-control-regex -- stripping control characters is the job
const ILLEGAL_CHARS = /[<>:"/\\|?*\x00-\x1f]+/g;

// Cap that leaves room for ".csv" (4) + a browser collision suffix like
// " (1)" (4). 200 = 255 (typical filesystem limit) - 4 - 4 - safety margin.
const MAX_FILENAME_LEN = 200;

const FALLBACK_NAME = "Untitled";

/**
 * Sanitize a single name component for filesystem safety.
 * Returns "" if nothing usable remains — callers decide the fallback.
 */
function sanitizeFilenamePart(input: string): string {
  if (!input) return "";
  return (
    input
      .replace(ILLEGAL_CHARS, "_")
      // Collapse internal whitespace runs to a single space
      .replace(/\s+/g, " ")
      // Strip leading whitespace, dots (Windows hates trailing dots)
      .replace(/^[\s.]+/, "")
      // Strip trailing whitespace and dots
      .replace(/[\s.]+$/, "")
  );
}

function capLength(s: string): string {
  return s.length <= MAX_FILENAME_LEN ? s : s.slice(0, MAX_FILENAME_LEN);
}

/**
 * Filename for a view export. Returns "<table> - <view>", or just one of the
 * two when the other is missing, or "Untitled" if both are unusable.
 *
 * When the view's display name equals the table name (default-view case),
 * collapses to just "<table>" to avoid silly "Sales - Sales" files.
 */
export function buildViewExportFilename(
  tableName: string,
  viewName: string,
): string {
  const t = sanitizeFilenamePart(tableName);
  const v = sanitizeFilenamePart(viewName);

  if (t && v) {
    return capLength(t === v ? t : `${t} - ${v}`);
  }
  if (t) return capLength(t);
  if (v) return capLength(v);
  return FALLBACK_NAME;
}

/**
 * Filename for a table export. Returns "<table>" or "Untitled".
 */
export function buildTableExportFilename(tableName: string): string {
  const t = sanitizeFilenamePart(tableName);
  return t ? capLength(t) : FALLBACK_NAME;
}
